import type { SummaryPageData } from '@core/presentationPrint';

interface SummaryPageProps {
  data: SummaryPageData;
}

/**
 * Final page — Summary comparison table + hot water comparison + confidence/flags/changes + next steps.
 * No scoring. No "recommended".
 */
export function SummaryPage({ data }: SummaryPageProps) {
  return (
    <div className="dp-page dp-summary-page">
      <section className="dp-summary-table-section">
        <h2>Summary</h2>
        <table className="dp-summary-table">
          <thead>
            <tr>
              <th>Option</th>
              <th>Comfort style</th>
              <th>Best for</th>
              <th>Typical annual cost</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i}>
                <td>{row.option}</td>
                <td>{row.comfortStyle}</td>
                <td>{row.bestFor}</td>
                <td>{row.typicalAnnualCost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Hot Water Comparison Table - addresses "See summary table" references */}
      {data.hotWaterComparison && data.hotWaterComparison.length > 0 && (
        <section className="dp-hot-water-comparison-section">
          <h2>Hot Water Comparison</h2>
          <table className="dp-hot-water-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Simultaneous Outlets</th>
                <th>Recovery Time</th>
                <th>Pressure</th>
                <th>Storage</th>
              </tr>
            </thead>
            <tbody>
              {data.hotWaterComparison.map((row, i) => (
                <tr key={i}>
                  <td>{row.option}</td>
                  <td>{row.simultaneousOutlets}</td>
                  <td>{row.recoveryTime}</td>
                  <td>{row.pressureDependency}</td>
                  <td>{row.storageVolume}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Data Completeness — global scope (how much measured data we have) */}
      {data.confidenceSummary && (
        <section className="dp-confidence-section">
          <h2>Data completeness: {data.confidenceSummary.level}</h2>
          <p className="dp-confidence-intro">
            Data completeness reflects how much of this report is based on
            your measured data versus typical assumptions for homes like yours.
          </p>
          {data.confidenceSummary.reasons.length > 0 && (
            <>
              <ul className="dp-confidence-reasons">
                {data.confidenceSummary.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
              <div className="dp-increase-confidence">
                <h3>To improve completeness:</h3>
                <ul className="dp-increase-confidence-list">
                  {data.confidenceSummary.reasons.slice(0, 3).map((reason, i) => {
                    let suggestion = '';
                    if (reason.toLowerCase().includes('assumed')) {
                      suggestion = 'Provide measured data for the assumed parameters';
                    } else if (reason.toLowerCase().includes('insulation')) {
                      suggestion = 'Get an energy audit to confirm insulation levels';
                    } else if (reason.toLowerCase().includes('radiator') || reason.toLowerCase().includes('emitter')) {
                      suggestion = 'Measure radiator outputs or have a system survey';
                    } else {
                      suggestion = 'Verify and provide actual data for assumed values';
                    }
                    return <li key={i}>{suggestion}</li>;
                  })}
                </ul>
              </div>
            </>
          )}
        </section>
      )}

      {/* Red Flags */}
      {data.redFlags && data.redFlags.length > 0 && (
        <section className="dp-red-flags-section">
          <h2>Important checks before confirming an installation:</h2>
          <ul className="dp-red-flags-list">
            {data.redFlags.map((flag, i) => (
              <li key={i} className={`dp-flag-${flag.severity}`}>
                <strong>{flag.title}:</strong> {flag.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* What Could Change */}
      {data.whatCouldChange && data.whatCouldChange.length > 0 && (
        <section className="dp-what-changes-section">
          <h2>What could change this recommendation:</h2>
          <ul className="dp-what-changes-list">
            {data.whatCouldChange.map((bullet, i) => (
              <li key={i}>{bullet}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="dp-next-steps-section">
        <h2>Next steps</h2>
        <ul className="dp-next-steps">
          {data.nextSteps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
